'use client';

import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import AppSpinner from '~/components/Loading/AppSpinner';
import LoadingOverlay from '~/components/Loading/LoadingOverlay';

interface RetainedStateViewProps<T> {
  data: T[] | undefined;
  isLoading: boolean;
  className?: string;
  renderItem: (item: T, index: number) => ReactNode;
  renderEmpty: () => ReactNode;
  SpinnerComponent?: ComponentType;
}

function RetainedStateView<T>({
  data,
  className,
  isLoading,
  renderItem,
  renderEmpty,
  SpinnerComponent,
}: RetainedStateViewProps<T>): ReactNode {
  const [retainedData, setRetainedData] = useState<T[]>(data || []);

  useEffect(() => {
    if (!isLoading && data) {
      setRetainedData(data);
    }
  }, [isLoading, data]);

  if (retainedData.length === 0 && isLoading) {
    return (
      <div className="h-[300px] flex items-center justify-center">
        {SpinnerComponent ? <SpinnerComponent /> : <AppSpinner />}
      </div>
    );
  }

  if (retainedData.length === 0 && !isLoading) {
    return renderEmpty();
  }

  return (
    <div className={`relative ${className}`}>
      {retainedData.map((item, index) => renderItem(item, index))}
      {isLoading && <LoadingOverlay />}
    </div>
  );
}

export { RetainedStateView };
